"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, ArrowRight, CircleCheck as CheckCircle2, Cpu, SlidersHorizontal } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
interface WorkflowStep {
  step: string;
  title: string;
  badge: string;
  description: string;
  highlights: string[];
}
const steps: WorkflowStep[] = [{
  step: "01",
  title: "In-Line Optical & Sensor Probing",
  badge: "Data Acquisition",
  description: "Direct process integration through non-invasive optical probes and robust sampling interfaces positioned inside reactors, pipelines, and flow cells without interrupting active production cycles.",
  highlights: ["Non-destructive real-time probing", "High-pressure and temperature resilience", "Sanitary and clean-in-place (CIP) compliance"]
}, {
  step: "02",
  title: "Rapid Real-Time Spectral Processing",
  badge: "Chemometric Analysis",
  description: "High-speed chemometric algorithms and multi-wavelength processing convert raw optical telemetry into immediate quantitative data within milliseconds, enabling continuous quality oversight.",
  highlights: ["Instantaneous calibration evaluation", "Multivariate statistical process control", "Sub-second measurement latency"]
}, {
  step: "03",
  title: "Automated Process Control Feedback",
  badge: "Closed-Loop Control",
  description: "Seamless integration with plant distributed control systems (DCS) and SCADA architectures triggers closed-loop parameter adjustments to eliminate out-of-spec batches and safeguard yields.",
  highlights: ["Direct SCADA and DCS connectivity", "Automated critical parameter correction", "Complete audit-ready batch records"]
}];
export default function Workflow() {
  const shouldReduceMotion = useReducedMotion();
  const getStepIcon = (index: number) => {
    switch (index) {
      case 0:
        return <Activity className="size-5 text-primary" />;
      case 1:
        return <Cpu className="size-5 text-primary" />;
      case 2:
      default:
        return <SlidersHorizontal className="size-5 text-primary" />;
    }
  };
  return <section id="workflowprocess" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16 lg:mb-20">
          <div className="inline-flex items-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3 py-1 text-xs uppercase tracking-wider font-semibold">
              Process Analytical Architecture
            </Badge>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground">
            Precision Analytical Workflow
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            A continuous three-stage workflow connecting high-accuracy probing, millisecond spectral computation, and direct automation feedback for regulated manufacturing environments.
          </p>
        </div>

        {/* 3-Column Workflow Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {steps.map((item, i) => <div data-index={i} key={item.step} className="flex">
              <motion.div initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-50px"
          }} transition={{
            duration: 0.5,
            delay: shouldReduceMotion ? 0 : i * 0.15,
            ease: "easeOut"
          }} className="w-full flex">
                <Card className="w-full relative flex flex-col justify-between border border-border/80 shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-300 rounded-xl overflow-hidden bg-card text-card-foreground">
                  {/* Orange Top Accent Bar */}
                  <div className="h-1.5 w-full bg-primary absolute top-0 inset-x-0" />

                  <div>
                    <CardHeader className="pt-8 pb-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="size-11 rounded-lg bg-secondary flex items-center justify-center border border-border">
                          {getStepIcon(i)}
                        </div>
                        <span className="text-2xl font-bold font-default text-muted-foreground/50 tracking-tight">
                          {item.step}
                        </span>
                      </div>
                      <Badge variant="outline" className="w-fit text-xs font-medium mb-2 border-border text-muted-foreground">
                        {item.badge}
                      </Badge>
                      <CardTitle className="text-xl font-bold font-default text-foreground leading-snug">
                        {item.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="space-y-6 pt-0 pb-6">
                      <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                        {item.description}
                      </CardDescription>

                      <div className="pt-2 border-t border-border/60">
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                          Key Capabilities
                        </p>
                        <ul className="space-y-2.5">
                          {item.highlights.map((highlight, hIndex) => <li data-index={hIndex} key={hIndex} className="flex items-start gap-2.5 text-xs sm:text-sm text-foreground/90">
                              <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                              <span>{highlight}</span>
                            </li>)}
                        </ul>
                      </div>
                    </CardContent>
                  </div>

                  <div className="px-6 pb-6 pt-2 border-t border-border/40 mt-auto">
                    <div className="flex items-center justify-between text-xs font-medium text-muted-foreground">
                      <span>Phase {item.step} Integration</span>
                      <span className="text-primary font-semibold">Active Monitoring</span>
                    </div>
                  </div>
                </Card>
              </motion.div>
            </div>)}
        </div>

        {/* Bottom CTA Block */}
        <div className="mt-14 lg:mt-18 text-center flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button asChild size="lg" className="w-full sm:w-auto">
            <Link to="/SolutionsPage">
              Explore Technical Solutions
              <ArrowRight className="size-4" />
            </Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
            <Link to="/ContactPage">
              Speak with an Analytical Specialist
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}